import { useCallback, useEffect, useMemo, useState } from 'react';
import { Loader2, Settings } from 'lucide-react';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Separator } from '@/components/ui/separator';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { formatDateTime, formatRelativeTime } from '@/lib/format';
import {
  type DeviceDetail,
  type DeviceSummary,
  getDevice,
  listDevices,
  updateDeviceConfig
} from '@/lib/web-api';
import type { RuntimeConfig } from '@/lib/runtime-config';

type DevicesPageProps = {
  runtimeConfig: RuntimeConfig;
  tenantId: string;
};

const statusLabel = (status?: DeviceSummary['config_status']): string => {
  switch (status) {
    case 'up_to_date':
      return 'Up to date';
    case 'stale':
      return 'Out of date';
    default:
      return 'Unknown';
  }
};

const statusVariant = (status?: DeviceSummary['config_status']): 'success' | 'warning' | 'default' => {
  switch (status) {
    case 'up_to_date':
      return 'success';
    case 'stale':
      return 'warning';
    default:
      return 'default';
  }
};

export const DevicesPage = ({ runtimeConfig, tenantId }: DevicesPageProps) => {
  const [devices, setDevices] = useState<DeviceSummary[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [selected, setSelected] = useState<DeviceDetail | null>(null);
  const [configText, setConfigText] = useState('');
  const [saving, setSaving] = useState(false);
  const [configError, setConfigError] = useState<string | null>(null);

  const deviceCount = devices.length;

  const fetchDevices = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const result = await listDevices(runtimeConfig, tenantId);
      setDevices(result);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Unable to load devices.');
    } finally {
      setLoading(false);
    }
  }, [runtimeConfig, tenantId]);

  useEffect(() => {
    void fetchDevices();
  }, [fetchDevices]);

  const openDevice = useCallback(async (deviceId: string) => {
    setDialogOpen(true);
    setSelected(null);
    setConfigText('');
    setConfigError(null);
    try {
      const detail = await getDevice(runtimeConfig, tenantId, deviceId);
      setSelected(detail);
      setConfigText(detail.config ? JSON.stringify(detail.config.config, null, 2) : '{\n  \n}');
    } catch (err) {
      setConfigError(err instanceof Error ? err.message : 'Unable to load device config.');
    }
  }, [runtimeConfig, tenantId]);

  const handleSave = useCallback(async () => {
    if (!selected?.device.device_id) {
      return;
    }
    setSaving(true);
    setConfigError(null);
    try {
      const parsed = JSON.parse(configText);
      await updateDeviceConfig(runtimeConfig, tenantId, selected.device.device_id, parsed);
      setDialogOpen(false);
      await fetchDevices();
    } catch (err) {
      setConfigError(err instanceof Error ? err.message : 'Unable to update config.');
    } finally {
      setSaving(false);
    }
  }, [configText, fetchDevices, runtimeConfig, selected, tenantId]);

  const selectedMeta = useMemo(() => {
    if (!selected) {
      return null;
    }
    return {
      name: selected.device.device_name || selected.device.device_id,
      platform: selected.device.platform || '—',
      clientVersion: selected.device.client_version || '—',
      lastSeen: selected.device.last_seen_unix_ms
        ? formatRelativeTime(selected.device.last_seen_unix_ms)
        : '—',
      lastSync: selected.device.last_config_sync_unix_ms
        ? formatRelativeTime(selected.device.last_config_sync_unix_ms)
        : '—',
      configVersion: selected.config?.config_version ?? 0,
      updatedAt: selected.config?.updated_at_unix_ms
        ? formatDateTime(selected.config.updated_at_unix_ms)
        : '—'
    };
  }, [selected]);

  return (
    <div className="space-y-6">
      <div className="flex flex-wrap items-center justify-between gap-4">
        <div>
          <p className="text-sm font-medium text-muted-foreground">Control plane</p>
          <h2 className="text-2xl font-semibold">Devices</h2>
        </div>
        <Button variant="outline" onClick={() => void fetchDevices()} disabled={loading}>
          Refresh
        </Button>
      </div>

      <Card>
        <CardHeader className="space-y-2">
          <CardTitle>Registered devices</CardTitle>
          <CardDescription>
            {deviceCount} device{deviceCount === 1 ? '' : 's'} reporting config sync status.
          </CardDescription>
        </CardHeader>
        <CardContent>
          {loading ? (
            <div className="flex items-center gap-2 text-sm text-muted-foreground">
              <Loader2 className="h-4 w-4 animate-spin" />
              Loading devices
            </div>
          ) : error ? (
            <div className="rounded-md border border-destructive/40 bg-destructive/5 px-4 py-3 text-sm text-destructive">
              {error}
            </div>
          ) : (
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Device</TableHead>
                  <TableHead>Platform</TableHead>
                  <TableHead>Last seen</TableHead>
                  <TableHead>Config sync</TableHead>
                  <TableHead>Status</TableHead>
                  <TableHead className="text-right">Manage</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {devices.map(device => (
                  <TableRow key={device.device_id}>
                    <TableCell>
                      <div className="space-y-1">
                        <p className="text-sm font-medium">
                          {device.device_name || device.device_id}
                        </p>
                        <p className="text-xs text-muted-foreground">{device.device_id}</p>
                      </div>
                    </TableCell>
                    <TableCell>{device.platform ?? '—'}</TableCell>
                    <TableCell>
                      {device.last_seen_unix_ms ? formatRelativeTime(device.last_seen_unix_ms) : '—'}
                    </TableCell>
                    <TableCell>
                      {device.last_config_sync_unix_ms
                        ? formatRelativeTime(device.last_config_sync_unix_ms)
                        : '—'}
                    </TableCell>
                    <TableCell>
                      <Badge variant={statusVariant(device.config_status)}>
                        {statusLabel(device.config_status)}
                      </Badge>
                    </TableCell>
                    <TableCell className="text-right">
                      <Button
                        size="sm"
                        variant="secondary"
                        onClick={() => void openDevice(device.device_id)}
                      >
                        <Settings className="mr-2 h-4 w-4" />
                        Configure
                      </Button>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          )}
        </CardContent>
      </Card>

      <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
        <DialogContent className="max-w-3xl">
          <DialogHeader>
            <DialogTitle>Device configuration</DialogTitle>
            <DialogDescription>
              Update the assigned config for this device. Changes are pushed immediately.
            </DialogDescription>
          </DialogHeader>
          {selectedMeta ? (
            <div className="space-y-4">
              <div className="grid gap-4 rounded-lg border border-border bg-muted/40 p-4 text-sm md:grid-cols-2">
                <div>
                  <p className="text-xs uppercase text-muted-foreground">Device</p>
                  <p className="font-medium">{selectedMeta.name}</p>
                </div>
                <div>
                  <p className="text-xs uppercase text-muted-foreground">Platform</p>
                  <p className="font-medium">{selectedMeta.platform}</p>
                </div>
                <div>
                  <p className="text-xs uppercase text-muted-foreground">Client version</p>
                  <p className="font-medium">{selectedMeta.clientVersion}</p>
                </div>
                <div>
                  <p className="text-xs uppercase text-muted-foreground">Last seen</p>
                  <p className="font-medium">{selectedMeta.lastSeen}</p>
                </div>
                <div>
                  <p className="text-xs uppercase text-muted-foreground">Config version</p>
                  <p className="font-medium">v{selectedMeta.configVersion}</p>
                </div>
                <div>
                  <p className="text-xs uppercase text-muted-foreground">Last updated</p>
                  <p className="font-medium">{selectedMeta.updatedAt}</p>
                </div>
              </div>

              <Separator />

              <div className="space-y-2">
                <p className="text-sm font-medium">Config JSON</p>
                <textarea
                  className="min-h-[260px] w-full rounded-md border border-border bg-background px-3 py-2 font-mono text-xs text-foreground shadow-sm focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-ring"
                  value={configText}
                  onChange={event => setConfigText(event.target.value)}
                />
                {configError ? (
                  <p className="text-sm text-destructive">{configError}</p>
                ) : (
                  <p className="text-xs text-muted-foreground">
                    Paste the repo assignments and settings in JSON format.
                  </p>
                )}
              </div>
            </div>
          ) : (
            <div className="flex items-center gap-2 text-sm text-muted-foreground">
              <Loader2 className="h-4 w-4 animate-spin" />
              Loading device configuration
            </div>
          )}
          <DialogFooter>
            <Button variant="outline" onClick={() => setDialogOpen(false)}>
              Cancel
            </Button>
            <Button onClick={() => void handleSave()} disabled={saving || !selectedMeta}>
              {saving ? (
                <>
                  <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                  Saving
                </>
              ) : (
                'Save config'
              )}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};
